import { Injectable } from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Initialize pdfMake virtual fonts
(pdfMake as any).vfs = (pdfFonts as any).vfs;

@Injectable({
  providedIn: 'root'
})
export class ReceiptPrintService {

  /**
   * Decides whether a column should be printed based on `display` and `download` properties:
   * 1. If display is false (or 'false'), definitely do NOT print.
   * 2. If download is false (or 'false'), do NOT print.
   * 3. In all rest of the cases, print.
   */
  isPrintableColumn(h: any): boolean {
    if (!h) return false;

    // Rule 1: If display is false, definitely NOT to print
    if (h.display === false || String(h.display).toLowerCase() === 'false') {
      return false;
    }

    // Rule 2: If download is false, NOT to print
    if (h.download === false || String(h.download).toLowerCase() === 'false') {
      return false;
    }

    // Rule 3: In rest of the cases, print
    return true;
  }

  /**
   * Sanitizes value for receipt display. Returns empty string if value is null, undefined, 'N/A', '---', '-', 'null', etc.
   */
  sanitizeValue(val: any): string {
    if (val === null || val === undefined) return '';
    const str = String(val).trim();
    const lower = str.toLowerCase();
    if (!str || lower === 'n/a' || str === '---' || str === '-' || lower === 'null' || lower === 'undefined' || lower === 'nan') {
      return '';
    }
    return str;
  }

  /**
   * Validates whether a coupon_number is present and valid.
   */
  isValidCouponNumber(couponNumber: any): boolean {
    if (couponNumber === null || couponNumber === undefined) return false;
    const str = String(couponNumber).trim();
    if (!str || str === '' || str.toUpperCase() === 'N/A' || str === '---' || str === '-' || str.toLowerCase() === 'null') {
      return false;
    }
    return true;
  }

  /**
   * Resolves human-readable Site Name and Site Address from a row or explicit siteId.
   */
  getSiteInfo(rowOrSiteId: any): { siteName: string; siteAddress: string; facilityName: string } {
    let siteName = '';
    let siteAddress = '';
    let facilityName = '';

    const siteListJson = localStorage.getItem('siteList');
    let sites: any[] = [];
    if (siteListJson) {
      try {
        sites = JSON.parse(siteListJson);
      } catch {}
    }

    if (rowOrSiteId && typeof rowOrSiteId === 'object') {
      siteName = rowOrSiteId.site_name || rowOrSiteId.sitename || '';
      siteAddress = rowOrSiteId.address || rowOrSiteId.site_address || rowOrSiteId.city || rowOrSiteId.location || '';
      facilityName = rowOrSiteId.facility_name || rowOrSiteId.company_name || '';

      const sId = rowOrSiteId.site_id || rowOrSiteId.site;
      if (sId && sites.length) {
        const match = sites.find(s => String(s.site_id) === String(sId) || String(s.id) === String(sId)) || sites[0];
        if (match) {
          if (!siteName && match.site_name) siteName = match.site_name;
          if (!facilityName && match.facility_name) facilityName = match.facility_name;
          if (!siteAddress && (match.address || match.city || match.location || match.short_code)) {
            siteAddress = match.address || match.city || match.location || match.short_code;
          }
        }
      }
    } else if (rowOrSiteId) {
      const sId = String(rowOrSiteId).trim();
      const match = sites.find(s => String(s.site_id) === sId || String(s.id) === sId) || sites[0];
      if (match) {
        siteName = match.site_name || '';
        facilityName = match.facility_name || '';
        siteAddress = match.address || match.city || match.location || match.short_code || '';
      }
    }

    if (!siteName) {
      siteName = localStorage.getItem('site_name') || localStorage.getItem('sitename') || '';
    }
    if (!facilityName && sites.length) {
      facilityName = sites[0]?.facility_name || '';
    }

    return { siteName, siteAddress, facilityName };
  }

  /**
   * Builds the exact 2-column receipt table body with Site Name, Dotted Subheader, Dynamic Fields, and Footer.
   */
  buildReceiptBody(receipt: any, headers: any[] = [], fallbackSiteId?: any): { body: any[]; hasSiteAddress: boolean } {
    const body: any[] = [];
    const siteInfo = this.getSiteInfo(receipt);
    const fallbackInfo = this.getSiteInfo(fallbackSiteId);

    const siteName = siteInfo.siteName || fallbackInfo.siteName;
    const siteAddress = siteInfo.siteAddress || fallbackInfo.siteAddress;

    let hasSiteAddress = false;

    // Header 1: Site Name
    if (siteName) {
      body.push([
        {
          text: siteName,
          alignment: 'center',
          bold: true,
          fontSize: 12,
          colSpan: 2,
          margin: [0, 2]
        },
        {}
      ]);

      // Header 2: Site Address / Location (separated by dotted line)
      if (siteAddress) {
        hasSiteAddress = true;
        body.push([
          {
            text: siteAddress,
            alignment: 'center',
            bold: true,
            fontSize: 11,
            colSpan: 2,
            margin: [0, 2]
          },
          {}
        ]);
      }
    }

    // Dynamic Fields from headers / receipt (excluding internal keys)
    const ignoredKeys = new Set(['id', '_id', 'actions', 'isDummy', 'site_id', 'site_name', 'sitename', 'site']);

    if (headers && headers.length > 0) {
      headers.forEach((h: any) => {
        if (!this.isPrintableColumn(h)) return;
        const key = h.key || h.field;
        if (!key || ignoredKeys.has(key)) return;

        let val = this.sanitizeValue(receipt?.[key]);
        if ((!val || val === '') && (key === 'company_name' || key === 'company' || key === 'companyName')) {
          val = siteInfo.facilityName || fallbackInfo.facilityName || '';
        }
        body.push([
          { text: h.name || h.title || h.headerName || key, bold: true, fontSize: 10 },
          { text: val, fontSize: 10 }
        ]);
      });
    } else {
      Object.keys(receipt || {}).forEach(key => {
        if (ignoredKeys.has(key)) return;
        let val = this.sanitizeValue(receipt[key]);
        if ((!val || val === '') && (key === 'company_name' || key === 'company' || key === 'companyName')) {
          val = siteInfo.facilityName || fallbackInfo.facilityName || '';
        }
        body.push([
          { text: key.replace(/_/g, ' ').toUpperCase(), bold: true, fontSize: 10 },
          { text: val, fontSize: 10 }
        ]);
      });
    }

    // Footer Note
    body.push([
      {
        text: 'Note : This is system generated receipt hence signature not required',
        colSpan: 2,
        bold: true,
        fontSize: 10,
        margin: [0, 3]
      },
      {}
    ]);

    return { body, hasSiteAddress };
  }

  /**
   * Generates and opens a single recharge receipt in a new tab using the exact design.
   */
  printSingleReceipt(data: any, fallbackSiteId?: any): void {
    const receipt = Array.isArray(data?.list) ? data.list[0] : data?.list || data;
    const headers = data?.header || [];

    const { body, hasSiteAddress } = this.buildReceiptBody(receipt, headers, fallbackSiteId);

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content: [
        {
          text: 'Recharge Receipt',
          alignment: 'center',
          bold: true,
          fontSize: 16,
          color: '#1B365D',
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            widths: ['40%', '60%'],
            body
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#000000',
            vLineColor: () => '#000000',
            hLineStyle: (i: number) => {
              if (hasSiteAddress && i === 1) {
                return { dash: { length: 2, space: 2 } };
              }
              return null;
            },
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ]
    };

    pdfMake.createPdf(docDefinition).open();
  }

  /**
   * Generates and opens multiple recharge receipts in a single PDF, with each receipt on a separate page.
   */
  printMultipleReceipts(rows: any[], headers: any[] = []): { success: boolean; error?: string } {
    if (!rows || rows.length === 0) {
      return { success: false, error: 'No records selected for printing.' };
    }

    // Step 1: Validate coupon_number for ALL selected rows
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const couponNumber = row.coupon_number || row.coupon_no || row.coupon_id || row.coupan_id || row.CouponId || row.couponNo;
      if (!this.isValidCouponNumber(couponNumber)) {
        const unitLabel = row.location_no || row.location_id || row.consumer_name || row.meter_no || `Record #${i + 1}`;
        return {
          success: false,
          error: `Coupon for ${unitLabel} cannot be printed because Coupon Number is missing or invalid.`
        };
      }
    }

    // Step 2: Build PDF content with exact design for each receipt
    const content: any[] = [];

    rows.forEach((row, index) => {
      const { body, hasSiteAddress } = this.buildReceiptBody(row, headers);

      const receiptBlock: any[] = [
        {
          text: 'Recharge Receipt',
          alignment: 'center',
          bold: true,
          fontSize: 16,
          color: '#1B365D',
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            widths: ['40%', '60%'],
            body
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#000000',
            vLineColor: () => '#000000',
            hLineStyle: (i: number) => {
              if (hasSiteAddress && i === 1) {
                return { dash: { length: 2, space: 2 } };
              }
              return null;
            },
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ];

      if (index < rows.length - 1) {
        receiptBlock.push({ text: '', pageBreak: 'after' });
      }

      content.push(...receiptBlock);
    });

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [40, 40, 40, 40],
      content
    };

    pdfMake.createPdf(docDefinition).open();
    return { success: true };
  }
}
